import React, { useMemo, useState } from 'react'
import { Avatar, Box, Tooltip, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { InstitutionDropdown } from '../AdminMenuItems/styles'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useNavigate } from 'react-router-dom'
import { CURRENT_ROLE_ID_KEY } from '../../../consts/roles'
import { type UserRole } from '../../../models/UserRoles'
import { blue, grey } from '../../../colors'
import { getCurrentRole } from '../../../helpers/roleHelpers'
import { useUserProfile } from '../../../hooks/useUserProfile'

const RoleDropdown: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // Compact avatar + tooltip from tablet through md; full label at lg+ (toolbar has more room)
  const isCompactRoleBadge = useMediaQuery(theme.breakpoints.between('sm', 'lg'))
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const navigate = useNavigate()
  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = (): void => {
    setAnchorEl(null)
  }

  const handleRoleSelect = async (role: UserRole): Promise<void> => {
    setAnchorEl(null)
    localStorage.setItem(CURRENT_ROLE_ID_KEY, role.roleId)
    navigate(window.location.href = '/')
  }
  const { data: userProfile } = useUserProfile()
  const currentUserRoles = userProfile?.roles ?? []

  const currentRole = useMemo(() => getCurrentRole(currentUserRoles), [currentUserRoles])
  const currentRoleId = localStorage.getItem(CURRENT_ROLE_ID_KEY)

  const getRoleInitials = (roleName: string | undefined): string => {
    if (!roleName) return ''
    const words = roleName?.trim()?.split(/\s+/)
    if (words.length === 1) return words[0]?.slice(0, 2)?.toUpperCase()
    return (words[0][0] + words[1][0])?.toUpperCase()
  }

  if (!currentUserRoles || currentUserRoles.length === 0) {
    return null
  }

  if (currentUserRoles.length === 1) {
    return null
  }

  const roleAvatarSx = {
    width: theme.spacing(7.5),
    height: theme.spacing(7.5),
    bgcolor: blue[300],
    color: grey[0],
    fontSize: '12px'
  }

  const roleBadge = currentRole
    ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          padding: `${theme.spacing(1.25)} ${theme.spacing(3.75)} ${theme.spacing(1.25)} ${theme.spacing(1.5)}`,
          borderRadius: theme.spacing(6),
          backgroundColor: blue[100],
          fontWeight: '400',
          fontSize: '14px',
          cursor: 'pointer'
        }}
      >
        <Avatar sx={roleAvatarSx}>
          {getRoleInitials(currentRole.roleName)}
        </Avatar>
        <Box component="span">
          {currentRole.roleName}
        </Box>
      </Box>
      )
    : (
      <Box component="span">Select Role</Box>
      )

  const compactRoleTooltip =
    currentRole != null
      ? `${currentRole.roleType}: ${currentRole.roleName}. Click to switch roles.`
      : ''

  return (
    <>
      <InstitutionDropdown
        isMobile={isMobile}
        isTablet={isCompactRoleBadge}
        active={open}
        aria-controls={open ? 'role-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={isCompactRoleBadge && currentRole != null ? compactRoleTooltip : undefined}
        onClick={handleDropdownClick}
      >
        {isCompactRoleBadge && currentRole != null
          ? (
            <Tooltip title={compactRoleTooltip} enterDelay={400}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: theme.spacing(0.75),
                  borderRadius: '50%',
                  backgroundColor: blue[100],
                  cursor: 'pointer'
                }}
              >
                <Avatar sx={roleAvatarSx}>
                  {getRoleInitials(currentRole.roleName)}
                </Avatar>
              </Box>
            </Tooltip>
            )
          : roleBadge}
      </InstitutionDropdown>
      <Menu
        id="role-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        MenuListProps={{ 'aria-labelledby': 'role-dropdown-button' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {currentUserRoles.map((role: UserRole) => (
          <MenuItem
            key={role?.roleId}
            selected={currentRoleId === role?.roleId}
            onClick={() => { void handleRoleSelect(role) }}
            sx={{ minWidth: 220, display: 'flex', gap: theme.spacing(2), alignItems: 'center', padding: `${theme.spacing(1.5)} ${theme.spacing(2)}` }}
          >
            <Avatar sx={roleAvatarSx}>
              {getRoleInitials(role.roleName)}
            </Avatar>
            <Box sx={{ fontWeight: 400, fontSize: '14px' }}>
              {role?.roleName}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default RoleDropdown
